/**
 * Feb 2026 · P0 — Central Online Discount Control (admin UI).
 * Route: /admin/settings/online-discounts
 *
 * ONE switch for the whole public site — turns online prepaid pricing +
 * every "Save X%" / "ONLINE PREPAID" / strike-through display on and off,
 * and edits the percentage + copy. Partner / PM / PWS / manual booking
 * flows are unaffected (they price through their own services).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  ArrowLeft, Save, RotateCcw, Loader2, AlertTriangle,
  CheckCircle2, Percent, Eye, EyeOff, RefreshCw,
} from "lucide-react";

const fmt = (v) => v == null ? "—" : Number(v).toLocaleString("en-AU", { style: "currency", currency: "AUD" });

export default function AdminOnlineDiscountSettings() {
  const [settings, setSettings] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const [s, p] = await Promise.all([
        api.get("/admin/settings/online-discounts"),
        api.get("/pricing"),
      ]);
      setSettings(s.data);
      setPricing(p.data);
    } catch (e) { setErr(formatApiError(e) || "Load failed"); }
    finally    { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/online-discounts", patch);
      setSettings(data);
      // Reload pricing so the preview reflects the new pct immediately.
      const p = await api.get("/pricing");
      setPricing(p.data);
      toast.success("Online discount settings updated — live everywhere.");
    } catch (e) { toast.error(formatApiError(e) || "Save failed."); }
    finally    { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset online discount settings to the built-in defaults (enabled · 20% · 'ONLINE PREPAID' · 'Save {pct}%' · strike-through on)?")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/settings/online-discounts/reset");
      setSettings(data);
      const p = await api.get("/pricing");
      setPricing(p.data);
      toast.success("Reset to defaults.");
    } catch (e) { toast.error(formatApiError(e) || "Reset failed."); }
    finally    { setSaving(false); }
  };

  if (loading && !settings) return <PortalShell title="Loading…" subtitle="Admin · Settings"><div className="text-slate-500">Loading…</div></PortalShell>;
  if (!settings) return <PortalShell title="Online Discount Settings" subtitle="Admin · Settings"><div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"><AlertTriangle className="w-4 h-4 inline mr-1" />{err || "Couldn't load."} <button onClick={load} className="ml-3 underline">retry</button></div></PortalShell>;

  const isEnabled = !!settings.enabled;
  const pct       = Number(settings.percentage || 0);
  const savings   = (settings.label_savings_template || "Save {pct}%").replace("{pct}", String(pct));

  return (
    <PortalShell title="Online Discount Control" subtitle="Admin · Settings">
      <div className="flex items-center justify-between mb-6">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" data-testid="online-discount-back"><ArrowLeft className="w-4 h-4" /> Admin</Link>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="online-discount-refresh" className="border-slate-300"><RefreshCw className="w-3.5 h-3.5" /></Button>
          <Button variant="outline" onClick={reset} disabled={saving} data-testid="online-discount-reset" className="border-slate-300"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset defaults</Button>
        </div>
      </div>

      <div className={`mb-6 rounded-2xl border p-5 ${isEnabled ? "border-cyan-200 bg-cyan-50/60" : "border-slate-200 bg-slate-50"}`}>
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-700">Online prepaid discount</div>
            <div className="text-lg font-bold text-slate-900 mt-0.5">{isEnabled ? `Active — ${savings} on public bookings` : "Disabled — retail-only pricing site-wide"}</div>
            <div className="text-xs text-slate-500 mt-1">Toggling this flips the switch for every public price display, all discount copy, and checkout totals. Partner / PM / franchisee pricing is not affected.</div>
          </div>
          <Switch checked={isEnabled} onCheckedChange={(v) => save({ enabled: !!v })} disabled={saving} data-testid="online-discount-enabled-toggle" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Left — settings */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-5">
          <h2 className="font-bold text-slate-900">Settings</h2>

          <label className="block">
            <span className="text-sm font-semibold text-slate-700 flex items-center gap-1"><Percent className="w-3.5 h-3.5" /> Discount percentage</span>
            <div className="mt-1 flex items-center gap-2">
              <Input type="number" min="0" max="90" value={pct} data-testid="online-discount-percentage" onChange={(e) => setSettings({ ...settings, percentage: e.target.value })} className="w-28" />
              <Button size="sm" onClick={() => save({ percentage: Math.max(0, Math.min(90, Number(settings.percentage))) })} disabled={saving} data-testid="online-discount-percentage-save" className="bg-cyan-600 hover:bg-cyan-700 text-white"><Save className="w-3.5 h-3.5 mr-1" /> Save</Button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Applies uniformly to every public test package. Per-package overrides on <Link to="/admin/pricing/test-catalog" className="underline">/admin/pricing/test-catalog</Link> still win when set.</p>
          </label>

          <label className="block">
            <span className="text-sm font-semibold text-slate-700">Pill label</span>
            <div className="mt-1 flex items-center gap-2">
              <Input value={settings.label_pill || ""} maxLength={60} data-testid="online-discount-pill-label" onChange={(e) => setSettings({ ...settings, label_pill: e.target.value })} />
              <Button size="sm" onClick={() => save({ label_pill: settings.label_pill })} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white"><Save className="w-3.5 h-3.5 mr-1" /> Save</Button>
            </div>
          </label>

          <label className="block">
            <span className="text-sm font-semibold text-slate-700">Savings copy template</span>
            <div className="mt-1 flex items-center gap-2">
              <Input value={settings.label_savings_template || ""} maxLength={120} data-testid="online-discount-savings-template" onChange={(e) => setSettings({ ...settings, label_savings_template: e.target.value })} />
              <Button size="sm" onClick={() => save({ label_savings_template: settings.label_savings_template })} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white"><Save className="w-3.5 h-3.5 mr-1" /> Save</Button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Use <code className="bg-slate-100 px-1 rounded">{"{pct}"}</code> to embed the current percentage.</p>
          </label>

          <label className="flex items-center justify-between gap-4 py-2">
            <span className="text-sm font-semibold text-slate-700">Show strike-through retail price</span>
            <Switch checked={!!settings.show_strikethrough} onCheckedChange={(v) => save({ show_strikethrough: !!v })} disabled={saving} data-testid="online-discount-strikethrough-toggle" />
          </label>

          {settings.updated_at && (
            <div className="text-[11px] text-slate-500 pt-3 border-t border-slate-100">Last updated {new Date(settings.updated_at).toLocaleString()} by {settings.updated_by || "system"}</div>
          )}
        </div>

        {/* Right — live preview */}
        <div data-testid="online-discount-preview" className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
          <h2 className="font-bold text-slate-900 flex items-center gap-2">{isEnabled ? <Eye className="w-4 h-4 text-cyan-700" /> : <EyeOff className="w-4 h-4 text-slate-400" />} Public price preview</h2>
          <p className="text-xs text-slate-500 mt-0.5">This is what visitors will see on the booking + package pages once you save.</p>
          <div className="mt-4 space-y-3">
            {(pricing?.tests || []).map((t) => (
              <div key={t.id || t.slug} className="rounded-xl bg-white border border-slate-200 p-4 flex items-center justify-between" data-testid={`online-discount-preview-${t.id || t.slug}`}>
                <div>
                  <div className="font-semibold text-slate-900">{t.name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{isEnabled && !!settings.label_pill && (<span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border border-cyan-200 bg-cyan-50 text-cyan-700 mr-1">{settings.label_pill}</span>)}{isEnabled && pct > 0 && (<span className="text-emerald-700 font-semibold">{savings}</span>)}</div>
                </div>
                <div className="text-right">
                  {isEnabled ? (
                    <>
                      <div className="text-lg font-bold text-cyan-700 tabular-nums">{fmt(t.online_prepaid)}</div>
                      {settings.show_strikethrough && t.retail !== t.online_prepaid && (<div className="text-[11px] text-slate-400 line-through tabular-nums">{fmt(t.retail)}</div>)}
                    </>
                  ) : (
                    <div className="text-lg font-bold text-slate-900 tabular-nums">{fmt(t.retail)}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className={`mt-4 rounded-lg p-3 text-xs ${isEnabled ? "bg-emerald-50 border border-emerald-100 text-emerald-800" : "bg-slate-100 border border-slate-200 text-slate-700"}`}>
            <div className="font-semibold">{isEnabled ? "Live: discount active" : "Live: retail-only"}</div>
            <div className="mt-0.5">
              {isEnabled
                ? <>Checkout totals use {fmt(pricing?.tests?.[0]?.online_prepaid)} for the AWARE 4-Point package. Stripe line items match this figure exactly.</>
                : <>Checkout totals use {fmt(pricing?.tests?.[0]?.retail)} for the AWARE 4-Point package. No "Save {pct}%" copy or strike-through will render.</>}
            </div>
          </div>
        </div>
      </div>
    </PortalShell>
  );
}
